import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import './Unsubscribe.css';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [language, setLanguage] = useState('de');
  const [emailType, setEmailType] = useState('');

  useEffect(() => {
    const lang = navigator.language?.startsWith('de') ? 'de' : 'en';
    setLanguage(lang);

    const unsubscribe = async () => {
      const token = searchParams.get('token');
      const type = searchParams.get('type') || 'weekly_reminders';
      setEmailType(type);
      
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${BACKEND_URL}/api/unsubscribe?token=${token}&email_type=${type}`);
        setStatus('success');
      } catch (error) {
        console.error('Unsubscribe error:', error);
        setStatus('error');
      }
    };

    unsubscribe();
  }, [searchParams]);

  const content = {
    de: {
      processing: {
        title: 'Abmeldung wird bearbeitet...',
        message: 'Bitte warten Sie einen Moment.'
      },
      success: {
        weekly_reminders: {
          title: '✅ Erfolgreich abgemeldet',
          message: 'Du erhältst keine wöchentlichen Erinnerungen mehr von StoryTales.',
          note: 'Du kannst diese Einstellung jederzeit in deinen Account-Einstellungen ändern.'
        },
        default: {
          title: '✅ Erfolgreich abgemeldet',
          message: 'Deine E-Mail-Einstellungen wurden aktualisiert.',
          note: 'Du kannst diese Einstellung jederzeit in deinen Account-Einstellungen ändern.'
        }
      },
      error: {
        title: '❌ Abmeldung fehlgeschlagen',
        message: 'Der Abmelde-Link ist ungültig oder abgelaufen. Bitte verwende die Einstellungen in deinem Account, um E-Mail-Benachrichtigungen zu verwalten.',
        button: 'Zu den Einstellungen'
      },
      homeButton: 'Zur Startseite',
      settingsButton: 'Zu den Einstellungen'
    },
    en: {
      processing: {
        title: 'Processing unsubscribe...',
        message: 'Please wait a moment.'
      },
      success: {
        weekly_reminders: {
          title: '✅ Successfully Unsubscribed',
          message: 'You will no longer receive weekly reminders from StoryTales.',
          note: 'You can change this setting anytime in your account settings.'
        },
        default: {
          title: '✅ Successfully Unsubscribed',
          message: 'Your email preferences have been updated.',
          note: 'You can change this setting anytime in your account settings.'
        }
      },
      error: {
        title: '❌ Unsubscribe Failed',
        message: 'The unsubscribe link is invalid or expired. Please use your account settings to manage email notifications.',
        button: 'Go to Settings'
      },
      homeButton: 'Go to Home',
      settingsButton: 'Go to Settings'
    }
  };

  const t = content[language];
  const successContent = status === 'success' 
    ? (t.success[emailType] || t.success.default)
    : null;

  return (
    <div className="unsubscribe-container">
      <div className="unsubscribe-card">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h1>{t.processing.title}</h1>
            <p>{t.processing.message}</p>
          </>
        )}
        
        {status === 'success' && successContent && (
          <>
            <h1>{successContent.title}</h1>
            <p className="message">{successContent.message}</p>
            {successContent.note && (
              <p className="note">{successContent.note}</p>
            )}
            <div className="button-group">
              <Button 
                onClick={() => navigate('/')}
                className="home-button"
              >
                {t.homeButton}
              </Button>
              <Button 
                onClick={() => navigate('/settings')}
                variant="outline"
                className="settings-button"
              >
                {t.settingsButton}
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <h1>{t.error.title}</h1>
            <p className="message">{t.error.message}</p>
            <div className="button-group">
              <Button 
                onClick={() => navigate('/')}
                className="home-button"
              >
                {t.homeButton}
              </Button>
              <Button 
                onClick={() => navigate('/settings')}
                variant="outline"
                className="settings-button"
              >
                {t.settingsButton}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
